import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole } from '../hooks/useUserRoleSecure';
import LoadingSpinner from './LoadingSpinner';
import { AlertTriangle } from 'lucide-react';
import { initiateLogin, isOnCallbackPage } from '../lib/authentik';

const ProtectedRoute = ({ children }) => {
  const { user, loading: authLoading, signOut } = useAuth();
  const { isDeactivated, loading: roleLoading } = useUserRole();
  const hasInitiatedLogin = useRef(false);

  // Auto-logout deactivated users
  useEffect(() => {
    if (isDeactivated && user) {
      console.log('User is deactivated, signing out...');
      signOut();
    }
  }, [isDeactivated, user, signOut]);

  // Clear login redirect guard when user is authenticated
  useEffect(() => {
    if (user) {
      sessionStorage.removeItem('sso_redirect_initiated');
    }
  }, [user]);

  // Auto-redirect to SSO if not authenticated
  // Use sessionStorage to prevent infinite loops across component remounts
  useEffect(() => {
    const alreadyRedirecting = sessionStorage.getItem('sso_redirect_initiated');

    if (!authLoading && !user && !isOnCallbackPage() && !hasInitiatedLogin.current && !alreadyRedirecting) {
      hasInitiatedLogin.current = true;
      sessionStorage.setItem('sso_redirect_initiated', Date.now().toString());
      console.log('No user session, redirecting to SSO...');
      initiateLogin();
    }
  }, [authLoading, user]);

  if (authLoading || roleLoading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }

  if (!user) {
    // Show loading while redirecting to SSO
    return <LoadingSpinner message="Redirecting to login..." />;
  }

  // Block deactivated users with a clear message
  if (isDeactivated) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        background: '#f9fafb'
      }}>
        <AlertTriangle size={64} color="#dc2626" style={{ marginBottom: '1rem' }} />
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '1rem', color: '#111827' }}>
          Account Deactivated
        </h1>
        <p style={{ fontSize: '1.125rem', color: '#6b7280', maxWidth: '500px', marginBottom: '2rem' }}>
          Your account has been deactivated. Please contact your administrator if you believe this is an error.
        </p>
        <button
          onClick={() => signOut()}
          style={{
            padding: '0.75rem 2rem',
            background: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Return to Login
        </button>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;